import React, { ReactNode, useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { initialTheme } from 'themes/constants'
import { applyTheme } from 'themes/utils'
import { DangerousElement } from 'shared/components/dangerous-element'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { SilentErrorBoundary } from 'shared/components/silent-error-boundary'
import { AccessDeniedReasonsEnum, getAccessDeniedText } from 'shared/errors/access-denied'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { cwd } from 'shared/utils/class-name-with-modifiers'
import { getSchoolUrl } from 'modules/school/utils/get-school-url'
import ThemeEditor from 'modules/theme-editor/components/theme-editor'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useGetCoursePageUrl } from '../hooks/use-course-path-url'
import { useIsUserAdminOrOwnerOfCourse } from '../hooks/use-is-user-admin-or-owner-of-course'
import useStepRouter from '../hooks/use-step-router'
import type { CourseDataInterface } from '../types/course-interface'
import { addPublicCustomClassNamePrefix } from '../utils/add-class-prefix'
import { CourseContentSkeleton } from './course-content-skeleton'
import { CourseAccessFromScreen } from './course-info-screens/course-access-from-screen'
import { CourseInfoScreenLayout } from './course-info-screens/course-info-screen-layout'
import { CourseLoginOrPurchaseScreen } from './course-info-screens/course-login-or-purchase-screen'
import { CourseLoginScreen } from './course-info-screens/course-login-screen'
import { CourseSearchForm } from './course-search-form'
import { CourseSideMenu } from './course-side-menu'
import MobileCourseSideMenu from './mobile-side-menu'

function CourseForm({
  courseData,
  hasAccess,
}: {
  courseData: CourseDataInterface | undefined
  hasAccess?: boolean
}): ReactNode {
  if (!courseData?.id) return null
  if (!hasAccess) return null
  return (
    <CourseSearchForm
      courseId={courseData.id}
      className="max-w-full shrink-0 grow basis-96 md:grow-0"
    />
  )
}

const CourseLayout = ({ children }: React.PropsWithChildren): ReactNode => {
  const router = useRouter()
  const { t } = useTranslation()
  const contentRef = useRef<HTMLDivElement>(null)

  const coursePath = useGetCoursePageUrl()
  const { user } = useUser()
  const isUserAdminOrOwnerOfCourse = useIsUserAdminOrOwnerOfCourse()
  const { courseData } = useCourseData()
  const { step } = useStepRouter()
  const { courseMenu, areAllModulesUnavailable } = useCourseMenu({
    courseId: courseData?.id,
  })

  const isAccessDenied = courseData?.isDeactivated && !isUserAdminOrOwnerOfCourse
  const isLoading = !user || !courseData || !courseMenu
  const salesPageLink = step?.module.salesPage || courseData?.salesPage

  const render = (): ReactNode => {
    if (!user || !courseData) {
      return <CourseContentSkeleton />
    }

    if (!isAuthorizedUser(user)) {
      if (salesPageLink) {
        return <CourseLoginOrPurchaseScreen salesPageLink={salesPageLink} />
      }

      return <CourseLoginScreen />
    }

    if (courseData.accessFrom) {
      return <CourseAccessFromScreen />
    }

    return children
  }

  useLayoutEffect(() => {
    applyTheme(initialTheme)
    if (courseData?.colorSet) applyTheme(courseData.colorSet)
  }, [courseData?.colorSet])

  const isCourseUnavailable =
    isAuthorizedUser(user) &&
    courseData &&
    !isAccessDenied &&
    !courseData.accessFrom &&
    areAllModulesUnavailable &&
    !isUserAdminOrOwnerOfCourse

  const isBlockedByDrip = courseMenu?.some(
    module => module.unavailabilityReason === 'dripping_access_is_not_granted_yet',
  )

  const hasCourseAccess = Boolean(
    isAuthorizedUser(user) &&
    !isAccessDenied &&
    !courseData?.accessFrom &&
    courseMenu &&
    areAllModulesUnavailable === false,
  )

  useEffect(() => {
    if (isCourseUnavailable) {
      if (!isBlockedByDrip && courseData.salesPage) {
        globalThis.location.assign(courseData.salesPage)
      } else {
        router.push(getSchoolUrl())
      }
    }
  }, [isCourseUnavailable, courseData, router, isBlockedByDrip])

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2
          data-testid="course-title"
          className={twMerge(
            'flex-auto text-3xl font-bold text-THEME-primary',
            isLoading && 'w-40 animate-pulse rounded-md bg-THEME-disabled text-transparent',
            cwd(addPublicCustomClassNamePrefix('course__title'), {
              ['loading']: isLoading,
            }),
          )}
        >
          <LinkWithoutPrefetch
            href={`/school/course/${coursePath}`}
            data-test-id="course-heading-name"
          >
            {courseData?.name || '.'}
          </LinkWithoutPrefetch>
        </h2>

        <CourseForm courseData={courseData} hasAccess={hasCourseAccess} />
      </div>

      <div className="mt-7 flex flex-col gap-3 md:gap-5 lg:flex-row lg:gap-12">
        <div
          className={`hidden shrink-0 md:block lg:mb-[-20px] ${cwd(
            addPublicCustomClassNamePrefix('course__side-menu-container'),
            {
              ['loading']: isLoading,
            },
          )}`}
        >
          <CourseSideMenu isLoading={isLoading} />
        </div>
        <div
          ref={contentRef}
          className={`flex min-w-0 basis-full ${addPublicCustomClassNamePrefix(
            'course__content-container',
          )}`}
        >
          {isAccessDenied ? (
            <CourseInfoScreenLayout
              text={
                <span className="whitespace-pre-line">
                  {t(getAccessDeniedText(AccessDeniedReasonsEnum.Deactivated))}
                </span>
              }
            />
          ) : (
            render()
          )}
        </div>
      </div>
      <ThemeEditor />
      <MobileCourseSideMenu className="fixed right-3 bottom-3 z-10 md:hidden" />
      {courseData?.embeddedCode && (
        <SilentErrorBoundary>
          <DangerousElement markup={courseData.embeddedCode} />
        </SilentErrorBoundary>
      )}
    </>
  )
}

export default CourseLayout
